export const SIDE_PANEL_HASH_PARAM = "spid";
export const SIDE_PANEL_TYPE_HASH_PARAM = "spt";
export const FULL_SCREEN_HASH_PARAM = "fullScreen";

export const AGENT_ACTIONS_SIDE_PANEL_TYPE = "actions";
export const INTERACTIVE_CONTENT_SIDE_PANEL_TYPE = "interactive_content";
export const ARTIFACT_PREVIEW_SIDE_PANEL_TYPE = "artifact_preview";
export const FILE_PREVIEW_SIDE_PANEL_TYPE = "file_preview";
export const FILES_SIDE_PANEL_TYPE = "files";
export const CREDITS_SIDE_PANEL_TYPE = "credits";
export const PLAN_SIDE_PANEL_TYPE = "plan";
export const SKILL_SIDE_PANEL_TYPE = "skill";
export const TOOL_REVIEW_SIDE_PANEL_TYPE = "tool_review";
export const DETAILS_SIDE_PANEL_TYPE = "details";
export const LIVE_DOC_SIDE_PANEL_TYPE = "live_doc";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SIDE_PANEL_TYPES = [
  DETAILS_SIDE_PANEL_TYPE,
  TOOL_REVIEW_SIDE_PANEL_TYPE,
  AGENT_ACTIONS_SIDE_PANEL_TYPE,
  INTERACTIVE_CONTENT_SIDE_PANEL_TYPE,
  ARTIFACT_PREVIEW_SIDE_PANEL_TYPE,
  FILE_PREVIEW_SIDE_PANEL_TYPE,
  FILES_SIDE_PANEL_TYPE,
  CREDITS_SIDE_PANEL_TYPE,
  PLAN_SIDE_PANEL_TYPE,
  SKILL_SIDE_PANEL_TYPE,
  LIVE_DOC_SIDE_PANEL_TYPE,
] as const;

export type ConversationSidePanelType =
  | (typeof SIDE_PANEL_TYPES)[number]
  | undefined;

/** Tags a file id inside the file preview panel's hash value. Paths stay untagged. */
export const FILE_PREVIEW_FILE_ID_PREFIX = "id:";

export type FilePreviewTarget =
  | { kind: "fileId"; fileId: string }
  | { kind: "path"; filePath: string };

/**
 * Which identifier the preview panel should key on.
 *
 * A mount path wins whenever there is one: the path route serves the converted PDF that
 * Office documents preview through, and the listing it resolves against also carries the
 * file id, so nothing is lost. Agent-generated files often have no mount path at all, and
 * those fall back to the id.
 */
export function filePreviewTargetFrom(file: {
  fileId?: string | null;
  filePath?: string | null;
}): FilePreviewTarget | null {
  if (file.filePath) {
    return { kind: "path", filePath: file.filePath };
  }
  if (file.fileId) {
    return { kind: "fileId", fileId: file.fileId };
  }
  return null;
}

export function serializeFilePreviewTarget(target: FilePreviewTarget): string {
  return target.kind === "fileId"
    ? `${FILE_PREVIEW_FILE_ID_PREFIX}${target.fileId}`
    : target.filePath;
}

/**
 * Untagged values are read as paths so links minted before the panel accepted file ids keep
 * working, as do `artifact_preview` links, which always carried a bare file id.
 */
export function parseFilePreviewTarget(
  data: string | undefined,
  panel: ConversationSidePanelType
): FilePreviewTarget | null {
  if (!data) {
    return null;
  }
  if (panel === ARTIFACT_PREVIEW_SIDE_PANEL_TYPE) {
    return { kind: "fileId", fileId: data };
  }
  if (data.startsWith(FILE_PREVIEW_FILE_ID_PREFIX)) {
    return {
      kind: "fileId",
      fileId: data.slice(FILE_PREVIEW_FILE_ID_PREFIX.length),
    };
  }
  return { kind: "path", filePath: data };
}
